import apiFunctions from '..';
import { failure, tooManyRequests, unauthorized } from '../../types/api';
import {
  KirbyDetailsIdentifier,
  KirbyFilterPropertyDefinition,
  KirbyFilterRequest,
  KirbyFilterResultPage,
} from '../../types/kirbyFiltering';
import { normalizeAvailableFilterTypes, normalizeFilterPropertyDefinitions } from '../../utility/kirbyFiltering';

const filteringApiRoot = 'Kirby/FilterDefinition';
const minimumDetailsRequestReasonLength = 30;
export const maximumDetailsRequestReasonLength = 500;

/** Replaces control characters so request reasons remain suitable for structured audit logging. */
export const sanitizeDetailsRequestReason = (requestReason: string): string =>
  Array.from(requestReason, (character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined && (codePoint <= 31 || codePoint === 127) ? ' ' : character;
  }).join('');

const filterEndpoints: Record<string, string | undefined> = {
  ApiClientCallLog: 'Kirby/ApiClientCallLog/Filter',
  EventLog: 'Kirby/EventLog/Filter',
  IngestApp: 'Kirby/IngestApp/Filter',
};

const detailsEndpoints: Record<string, string | undefined> = {
  ApiClientCallLog: 'Kirby/ApiClientCallLog/GetDetails',
  EventLog: 'Kirby/EventLog/GetDetails',
  IngestApp: 'Kirby/IngestApp/GetDetails',
};

const assertSuccessfulResponse = (response: unknown): unknown => {
  if (response === failure || response === unauthorized || response === tooManyRequests) {
    throw new Error(String(response));
  }

  return response;
};

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const isUuid = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const isValidDetailsIdentifier = (filterType: string, id: unknown): id is KirbyDetailsIdentifier =>
  filterType === 'IngestApp' ? isUuid(id) : isNonNegativeInteger(id);

const normalizeFilterResultPage = (response: unknown): KirbyFilterResultPage => {
  if (typeof response !== 'object' || response === null) {
    throw new Error('Kirby returned an invalid filter result envelope.');
  }

  const { pageNumber, pageSize, results, totalCount } = response as Record<string, unknown>;
  const records = Array.isArray(results)
    ? results.filter(
        (result): result is Record<string, unknown> =>
          typeof result === 'object' && result !== null && !Array.isArray(result),
      )
    : [];

  if (
    !isNonNegativeInteger(pageNumber) ||
    !isNonNegativeInteger(pageSize) ||
    !isNonNegativeInteger(totalCount) ||
    pageNumber < 1 ||
    pageSize < 1 ||
    !Array.isArray(results) ||
    records.length !== results.length
  ) {
    throw new Error('Kirby returned an invalid filter result envelope.');
  }

  return { pageNumber, pageSize, totalCount, results: records };
};

/** Returns the verified result endpoint for a filter category, when available. */
export const getFilterEndpoint = (filterType: string): string | undefined =>
  Object.hasOwn(filterEndpoints, filterType) ? filterEndpoints[filterType] : undefined;

/** Returns the verified details endpoint for a filter category, when available. */
export const getDetailsEndpoint = (filterType: string): string | undefined =>
  Object.hasOwn(detailsEndpoints, filterType) ? detailsEndpoints[filterType] : undefined;

/** Returns the safe detail identifier exposed by a result record for its supported category. */
export const getDetailsIdentifier = (
  filterType: string,
  record: Record<string, unknown>,
): KirbyDetailsIdentifier | undefined => {
  const id = filterType === 'IngestApp' ? record.appUid : record.id;
  return isValidDetailsIdentifier(filterType, id) ? id : undefined;
};

/** Loads the filter categories currently advertised by Kirby. */
export const getAvailableFilterTypes = async (): Promise<string[]> => {
  const response = assertSuccessfulResponse(await apiFunctions.apiPost(`${filteringApiRoot}/GetAvailableFilterTypes`));
  const filterTypes = normalizeAvailableFilterTypes(response);
  if (filterTypes.length === 0) throw new Error('Kirby returned no valid filter categories.');

  return filterTypes;
};

/** Loads and normalizes the property definitions for one filter category. */
export const getFilterPropertyDefinitions = async (filterType: string): Promise<KirbyFilterPropertyDefinition[]> => {
  const response = assertSuccessfulResponse(
    await apiFunctions.apiPost(`${filteringApiRoot}/GetFilterPropertyDefinitions`, { FilterType: filterType }),
  );
  const definitions = normalizeFilterPropertyDefinitions(response);
  if (definitions.length === 0) throw new Error('Kirby returned no valid property definitions.');

  return definitions;
};

/** Executes a validated filter against an explicitly allowlisted Kirby endpoint. */
export const executeFilter = async (
  filterType: string,
  request: KirbyFilterRequest,
  signal?: AbortSignal,
): Promise<KirbyFilterResultPage> => {
  const endpoint = getFilterEndpoint(filterType);
  if (!endpoint) throw new Error(`Filtering is not yet available for ${filterType}.`);

  const response = assertSuccessfulResponse(
    await (signal ? apiFunctions.apiPost(endpoint, request, signal) : apiFunctions.apiPost(endpoint, request)),
  );
  return normalizeFilterResultPage(response);
};

/** Retrieves one result record's raw details after the caller provides an audit reason. */
export const executeDetails = async (
  filterType: string,
  id: KirbyDetailsIdentifier,
  requestReason: string,
): Promise<unknown> => {
  const endpoint = getDetailsEndpoint(filterType);
  if (!endpoint) throw new Error(`Details are not yet available for ${filterType}.`);
  if (!isValidDetailsIdentifier(filterType, id)) {
    throw new Error('A valid record identifier is required to retrieve details.');
  }
  const trimmedRequestReason = sanitizeDetailsRequestReason(requestReason).trim();
  if (trimmedRequestReason.length < minimumDetailsRequestReasonLength) {
    throw new Error(`A request reason of at least ${minimumDetailsRequestReasonLength} characters is required.`);
  }
  if (trimmedRequestReason.length > maximumDetailsRequestReasonLength) {
    throw new Error(`A request reason cannot exceed ${maximumDetailsRequestReasonLength} characters.`);
  }

  return assertSuccessfulResponse(
    await apiFunctions.apiPost(endpoint, { Id: id, RequestReason: trimmedRequestReason }),
  );
};
